import { useQuery } from '@apollo/client';
import Risk from '@clearkit/icons/glyphs/Risk';
import Radio from '@material-ui/core/Radio';
import ToggleButton from '@material-ui/lab/ToggleButton';
import { CKBanner, CKBannerVariant, CKButton } from 'clearkit';
import { Link } from 'react-router-dom';

import Tile from '~/components/audiences/Tile';
import ContentBox from '~/components/ContentBox';
import GraphqlError from '~/components/GraphqlError';
import InfoTooltip from '~/components/InfoTooltip';
import LoadingArea from '~/components/LoadingArea';
import Select from '~/components/RedesignedSelect';
import TextField from '~/components/RedesignedTextField';
import ToggleRadioButtonGroup from '~/components/ToggleRadioButtonGroup';
import FacebookIcon from '~/images/services/Facebook.svg';
import GoogleIcon from '~/images/services/Google.svg';
import {
  audienceServices,
  audienceSubtypes,
  audienceSubtypeServices,
  readableAudienceSubtype,
} from '~/lib/audiences';
import { authConnector } from '~/lib/client';
import { humanReadableErrorMessage } from '~/lib/errors';
import { errorMessage, hasError } from '~/lib/graphql';
import paths from '~/lib/paths';
import RadioChecked from '~/pages/destinations/components/TriggerType/RadioChecked.svg';
import RadioUnchecked from '~/pages/destinations/components/TriggerType/RadioUnchecked.svg';

import AudienceFormWarning from './AudienceFormWarning';
import FacebookTerms from './FacebookTerms';
import query from './query.graphql';
import styles from './styles';

function AudienceNewPageSyncStep({
  className,
  classes,
  errors,
  facebookAccountConnector,
  googleAccountConnector,
  loading,
  onChange,
  onValidate,
  refetch,
  values,
  values: {
    id,
    config: {
      externalAccountId,
      facebookAccountId,
      facebookPixelId,
      googleAnalyticsAccountId,
      googleAnalyticsProfileId,
      googleAnalyticsPropertyId,
      googleAccountId,
    } = {},
    externalName,
    name,
    segment = {},
    service,
    subtype,
  },
  syncToGoogle,
  syncToManagedGoogle,
}) {
  const [facebookTermsAccepted, setFacebookTermsAccepted] = useState(null);
  const facebookAdAccounts = facebookAccountConnector?.adAccounts || [];

  const facebookAdAccount = facebookAdAccounts.find((adAccount) => {
    return adAccount.id === facebookAccountId;
  });

  const googleAccounts = googleAccountConnector?.remoteMeta?.accounts || [];

  const googleAdAccounts = googleAccountConnector?.adAccounts || [];

  const syncToFacebook = audienceSubtypeServices(subtype).includes(
    audienceServices.facebook,
  );

  useEffect(() => {
    if (service === audienceServices.facebook) {
      setFacebookTermsAccepted(facebookAdAccount?.termsAccepted);
    }
  }, [facebookAccountId]);

  useEffect(() => {
    let validation = {
      service: !!service,
      externalName: !!externalName,
    };

    if (service === audienceServices.facebook) {
      validation = {
        ...validation,
        facebookAccountTerms: facebookTermsAccepted,
        facebookAccountId: !!facebookAccountId,
      };

      if (subtype === audienceSubtypes.visitors) {
        validation = {
          ...validation,
          facebookPixelId: !!facebookPixelId,
        };
      }
    } else if (syncToGoogle && service === audienceServices.google) {
      validation = {
        ...validation,
        googleAnalyticsAccountId,
        googleAccountId,
        googleAnalyticsPropertyId,
        googleAnalyticsProfileId,
      };
    } else if (service === audienceServices.google) {
      validation = {
        ...validation,
        externalAccountId: !!externalAccountId,
      };
    }

    onValidate(validation);
  }, [
    service,
    externalAccountId,
    externalName,
    facebookAccountId,
    facebookTermsAccepted,
    facebookPixelId,
    googleAccountId,
    googleAnalyticsAccountId,
    googleAnalyticsPropertyId,
    googleAnalyticsProfileId,
  ]);

  return (
    <div className={className}>
      {(() => {
        switch (subtype) {
          case audienceSubtypes.companySegmentProspects:
          case audienceSubtypes.contacts:
          case audienceSubtypes.managedVisitors:
          case audienceSubtypes.visitors:
            return (
              !values.segment && (
                <div className="mb-5">
                  <CKBanner
                    variant={CKBannerVariant.warning}
                    heading="Select an audience to continue"
                    description="Create your audience from a new or existing audience."
                    icon={<Risk className="text-yellow-700" />}
                    cta={
                      <CKButton
                        as={Link}
                        to={paths.audienceNew(subtype)}
                        variant="bold"
                        variantColor="yellow">
                        Select or create an audience
                      </CKButton>
                    }
                  />
                </div>
              )
            );
        }
      })()}
      <ContentBox>
        <ContentBox.Group>
          <ContentBox.Section variant="horizontal">
            <ContentBox.Icon>
              <Tile audience={values} size={40} />
            </ContentBox.Icon>
            <div className={classes.contentBit}>
              {(name || segment?.name) && <span>{name || segment.name}</span>}
              <span>
                {(!segment?.id || !id) && 'New '}{' '}
                {readableAudienceSubtype(subtype)} audience
              </span>
            </div>

            {/* <div className={classes.healthIndicator}>
                <HealthIndicator
                  health={2}
                  style={{ marginRight: 10 }}
                  variant="white"
                />
                9,213 matches
              </div> */}
          </ContentBox.Section>
        </ContentBox.Group>
        <div className={classes.syncTo}>
          <span className={classes.beforeLine} />
          sync to
          <span className={classes.beforeLine} />
        </div>
        {loading ? (
          <LoadingArea loading />
        ) : (
          <ToggleRadioButtonGroup
            className={classes.toggle}
            classes={{ root: classes.groupRoot }}
            exclusive
            onChange={(_event, service) => {
              onChange({
                service,
              });
            }}
            value={service}>
            {syncToFacebook && (
              <ToggleButton
                component="div"
                classes={{
                  root: classnames(classes.option, {
                    [classes.onlyOption]: !syncToGoogle && !syncToManagedGoogle,
                  }),
                  label: classes.optionLabel,
                }}
                value={audienceServices.facebook}>
                {facebookAccountConnector ? (
                  <>
                    <div className={classes.optionHeader}>
                      <ToggleRadio
                        classes={{ root: classes.radio }}
                        checked={service === audienceServices.facebook}
                      />
                      <FacebookIcon
                        width={36}
                        height={36}
                        className={classes.serviceIcon}
                      />
                      <div className={classes.contentBit}>
                        <span>Facebook Custom Audience</span>
                        <span>Syncs your audience to Facebook</span>
                      </div>
                    </div>

                    <div className={classes.optionContent}>
                      <div className={classes.field}>
                        <Select
                          disabled={service !== audienceServices.facebook}
                          error={
                            service === audienceServices.facebook &&
                            hasError(errors, 'facebookAccountId')
                          }
                          rel="facebook-choose-account"
                          label="Facebook account"
                          onChange={(event) => {
                            onChange({
                              config: {
                                facebookAccountId: event.target.value,
                              },
                            });
                          }}
                          placeholder="Choose a Facebook account"
                          required
                          value={facebookAccountId}>
                          {facebookAdAccounts.map((account) => (
                            <Select.MenuItem
                              key={account.id}
                              value={account.id}>
                              {account.name} - {account.id}
                            </Select.MenuItem>
                          ))}
                        </Select>
                        {service === audienceServices.facebook &&
                          hasError(errors, 'facebookAccountId') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(errors, 'facebookAccountId'),
                              )}{' '}
                              Please{' '}
                              <a
                                href="https://help.clearbit.com/hc/en-us/articles/360043723133-Troubleshooting-Facebook-Ads"
                                rel="noopener noreferrer"
                                target="_blank">
                                visit the docs
                              </a>{' '}
                              for more information.
                            </p>
                          )}
                      </div>

                      {subtype === audienceSubtypes.visitors && (
                        <div className={classes.field}>
                          <Select
                            label={
                              <>
                                Facebook Pixel*{' '}
                                <InfoTooltip title="The Facebook Pixel you want to build this audience with." />
                              </>
                            }
                            placeholder="Choose a Facebook Pixel"
                            error={
                              service === audienceServices.facebook &&
                              hasError(errors, 'facebookPixelId')
                            }
                            disabled={
                              service !== audienceServices.facebook ||
                              !facebookAccountId
                            }
                            className={classes.select}
                            onChange={(event) => {
                              onChange({
                                config: {
                                  facebookPixelId: event.target.value,
                                },
                              });
                            }}
                            value={facebookPixelId}>
                            {facebookAdAccount &&
                              facebookAdAccount.adPixels.map((pixel) => (
                                <Select.MenuItem
                                  key={pixel.id}
                                  value={pixel.id}>
                                  {pixel.name} - {pixel.id}
                                </Select.MenuItem>
                              ))}
                          </Select>
                          {service === audienceServices.facebook &&
                            hasError(errors, 'facebookPixelId') && (
                              <p className={classes.error}>
                                {humanReadableErrorMessage(
                                  errorMessage(errors, 'facebookPixelId'),
                                )}
                              </p>
                            )}
                        </div>
                      )}

                      <div className={classes.field}>
                        <TextField
                          disabled={service !== audienceServices.facebook}
                          error={
                            service === audienceServices.facebook &&
                            hasError(errors, 'externalName')
                          }
                          fullWidth
                          label="New Facebook Custom Audience name"
                          onChange={(event) => {
                            onChange({
                              externalName: event.target.value,
                            });
                          }}
                          placeholder="Name your Facebook Custom Audience"
                          rel="audience-external-name"
                          required
                          value={
                            service === audienceServices.facebook
                              ? externalName
                              : ''
                          }
                        />
                        {service === audienceServices.facebook &&
                          hasError(errors, 'externalName') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(errors, 'externalName'),
                              )}
                            </p>
                          )}
                      </div>
                    </div>
                    {facebookAdAccount && facebookTermsAccepted === false ? (
                      <div className={classes.field}>
                        <FacebookTerms
                          isDisabled={service !== audienceServices.facebook}
                          facebookAdAccountId={facebookAdAccount.id}
                          termsUrl={facebookAdAccount.termsUrl}
                          onTermsAccepted={() => {
                            setFacebookTermsAccepted(true);
                          }}
                        />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className={classes.disconnected}>
                    <div className={classes.svgWrapper}>
                      <FacebookIcon />
                    </div>
                    <CKButton
                      onClick={() =>
                        authConnector('facebook', () => refetch())
                      }>
                      Connect to Facebook
                    </CKButton>
                  </div>
                )}
              </ToggleButton>
            )}
            {syncToGoogle && (
              <ToggleButton
                component="div"
                classes={{
                  root: classnames(classes.option, {
                    [classes.onlyOption]: !syncToFacebook,
                  }),
                  label: classes.optionLabel,
                }}
                value={audienceServices.google}>
                {googleAccountConnector ? (
                  <>
                    <div className={classes.optionHeader}>
                      <ToggleRadio
                        classes={{ root: classes.radio }}
                        checked={service === audienceServices.google}
                      />
                      <GoogleIcon
                        width={36}
                        height={36}
                        className={classes.serviceIcon}
                      />
                      <div className={classes.contentBit}>
                        <span>Google Remarketing Audience</span>
                        <span>Syncs your audience to Google</span>
                      </div>
                    </div>

                    <div className={classes.optionContent}>
                      <div className={classes.field}>
                        <Select
                          disabled={service !== audienceServices.google}
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'googleAccountId')
                          }
                          label="Google Analytics account"
                          onChange={(event) => {
                            onChange({
                              config: {
                                googleAnalyticsAccountId: event.target.value,
                              },
                            });
                          }}
                          placeholder="Choose a Google account"
                          required
                          value={googleAnalyticsAccountId}>
                          {googleAccounts.map((account) => (
                            <Select.MenuItem
                              key={account.id}
                              value={account.id}>
                              {account.name} - {account.id}
                            </Select.MenuItem>
                          ))}
                        </Select>
                        {service === audienceServices.google &&
                          hasError(errors, 'googleAnalyticsAccountId') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(
                                  errors,
                                  'googleAnalyticsAccountId',
                                ),
                              )}
                            </p>
                          )}
                      </div>

                      <div className={classes.field}>
                        <Select
                          placeholder="Choose a Google Analytics Property"
                          disabled={!googleAnalyticsAccountId}
                          label={
                            <>
                              Google Analytics Property *{' '}
                              <InfoTooltip title="The Google Analytics Property you want to build this audience with." />
                            </>
                          }
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'googleAnalyticsPropertyId')
                          }
                          className={classes.select}
                          onChange={(event) => {
                            onChange({
                              config: {
                                googleAnalyticsPropertyId: event.target.value,
                              },
                            });
                          }}
                          value={googleAnalyticsPropertyId}>
                          {googleAccountConnector.remoteMeta.properties
                            ?.filter((property) => {
                              return (
                                property.accountId === googleAnalyticsAccountId
                              );
                            })
                            .map((property) => (
                              <Select.MenuItem
                                key={property.id}
                                value={property.id}>
                                {property.name} - {property.id}
                              </Select.MenuItem>
                            ))}
                        </Select>
                        {service === audienceServices.google &&
                          hasError(errors, 'googleAnalyticsPropertyId') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(
                                  errors,
                                  'googleAnalyticsPropertyId',
                                ),
                              )}
                            </p>
                          )}
                      </div>

                      <div className={classes.field}>
                        <Select
                          placeholder="Choose a Google Analytics View"
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'googleAnalyticsProfileId')
                          }
                          disabled={!googleAnalyticsPropertyId}
                          label={
                            <>
                              Google Analytics View *{' '}
                              <InfoTooltip title="The Google Analytics View you want to build this audience with." />
                            </>
                          }
                          className={classes.select}
                          onChange={(event) => {
                            onChange({
                              config: {
                                googleAnalyticsProfileId: event.target.value,
                              },
                            });
                          }}
                          value={googleAnalyticsProfileId}>
                          {googleAccountConnector.remoteMeta.profiles
                            ?.filter((profile) => {
                              return (
                                profile.propertyId === googleAnalyticsPropertyId
                              );
                            })
                            .map((profile) => (
                              <Select.MenuItem
                                key={profile.id}
                                value={profile.id}>
                                {profile.name} - {profile.id}
                              </Select.MenuItem>
                            ))}
                        </Select>
                        {service === audienceServices.google &&
                          hasError(errors, 'googleAnalyticsProfileId') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(
                                  errors,
                                  'googleAnalyticsProfileId',
                                ),
                              )}
                            </p>
                          )}
                      </div>

                      <div className={classes.field}>
                        <Select
                          placeholder="Choose a Google Ads account"
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'googleAccountId')
                          }
                          label={
                            <>
                              Google Ads account *{' '}
                              <InfoTooltip title="The Google Ads account to link with the audience." />
                            </>
                          }
                          disabled={!googleAnalyticsProfileId}
                          className={classes.select}
                          onChange={(event) => {
                            onChange({
                              config: {
                                googleAccountId: event.target.value,
                              },
                            });
                          }}
                          value={googleAccountId}>
                          {googleAccountConnector.remoteMeta.adWordsAccounts
                            ?.filter((adWordsAccount) => {
                              return (
                                adWordsAccount.propertyId ===
                                googleAnalyticsPropertyId
                              );
                            })
                            ?.map((adWordsAccount) => (
                              <Select.MenuItem
                                key={adWordsAccount.id}
                                value={adWordsAccount.id}>
                                {adWordsAccount.name} - {adWordsAccount.id}
                              </Select.MenuItem>
                            ))}
                        </Select>
                        {service === audienceServices.google &&
                          hasError(errors, 'googleAccountId') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(errors, 'googleAccountId'),
                              )}
                            </p>
                          )}
                      </div>

                      <div className={classes.field}>
                        <TextField
                          disabled={!googleAccountId}
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'externalName')
                          }
                          fullWidth
                          label="New Google Remarketing Audience name"
                          onChange={(event) => {
                            onChange({
                              externalName: event.target.value,
                            });
                          }}
                          placeholder="Name your Google Remarketing Audience"
                          rel="audience-external-name"
                          required
                          value={
                            service === audienceServices.google
                              ? externalName
                              : ''
                          }
                          variant="standard"
                        />
                        {service === audienceServices.google &&
                          hasError(errors, 'externalName') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(errors, 'externalName'),
                              )}
                            </p>
                          )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={classes.disconnected}>
                    <div className={classes.svgWrapper}>
                      <GoogleIcon />
                    </div>
                    <CKButton
                      onClick={() =>
                        authConnector('google_oauth2', () => refetch())
                      }>
                      Connect to Google
                    </CKButton>
                  </div>
                )}
              </ToggleButton>
            )}

            {syncToManagedGoogle && (
              <ToggleButton
                component="div"
                classes={{
                  root: classnames(classes.option, {
                    [classes.onlyOption]: !syncToFacebook,
                  }),
                  label: classes.optionLabel,
                }}
                value={audienceServices.google}>
                {googleAccountConnector?.adAccounts ? (
                  <>
                    <div className={classes.optionHeader}>
                      <ToggleRadio
                        classes={{ root: classes.radio }}
                        checked={service === audienceServices.google}
                      />
                      <GoogleIcon
                        width={36}
                        height={36}
                        className={classes.serviceIcon}
                      />
                      <div className={classes.contentBit}>
                        <span>Google Remarketing Audience</span>
                        <span>Syncs your audience to Google</span>
                      </div>
                    </div>

                    <div className={classes.optionContent}>
                      <div className={classes.field}>
                        <Select
                          className={classes.select}
                          disabled={service !== audienceServices.google}
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'externalAccountId')
                          }
                          onChange={(event) => {
                            onChange({
                              config: {
                                externalAccountId: event.target.value,
                              },
                            });
                          }}
                          label="Google account"
                          placeholder="Choose a Google account"
                          required
                          value={externalAccountId}>
                          {googleAdAccounts.map((account) => (
                            <Select.MenuItem
                              key={account.id}
                              value={account.id}>
                              {account.name} - {account.id}
                            </Select.MenuItem>
                          ))}
                        </Select>
                        {service === audienceServices.google &&
                          hasError(errors, 'externalAccountId') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(errors, 'externalAccountId'),
                              )}
                            </p>
                          )}
                      </div>

                      <div className={classes.field}>
                        <TextField
                          disabled={!externalAccountId}
                          error={
                            service === audienceServices.google &&
                            hasError(errors, 'externalName')
                          }
                          fullWidth
                          label="New Google Remarketing Audience name"
                          onChange={(event) => {
                            onChange({
                              externalName: event.target.value,
                            });
                          }}
                          placeholder="Name your Google Remarketing Audience"
                          rel="audience-external-name"
                          required
                          value={
                            service === audienceServices.google
                              ? externalName
                              : ''
                          }
                          variant="standard"
                        />
                        {service === audienceServices.google &&
                          hasError(errors, 'externalName') && (
                            <p className={classes.error}>
                              {humanReadableErrorMessage(
                                errorMessage(errors, 'externalName'),
                              )}
                            </p>
                          )}
                      </div>
                    </div>
                    {hasError(errors, 'googleCustomerMatchNotSupported') ? (
                      <div className={classes.field}>
                        <AudienceFormWarning
                          message={
                            <p>
                              To use Clearbit Audiences within Google Ads, the
                              selected account must first be approved by Google
                              to use Customer Match.{' '}
                              <a href="https://support.google.com/adspolicy/answer/6299717">
                                Click here
                              </a>{' '}
                              to learn more about getting approved by Google for
                              Customer Match.
                            </p>
                          }
                        />
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className={classes.disconnected}>
                    <div className={classes.svgWrapper}>
                      <GoogleIcon />
                    </div>
                    <CKButton
                      onClick={() =>
                        authConnector('google_oauth2', () => refetch())
                      }>
                      Connect to Google
                    </CKButton>
                  </div>
                )}
              </ToggleButton>
            )}
          </ToggleRadioButtonGroup>
        )}
      </ContentBox>
    </div>
  );
}

AudienceNewPageSyncStep.propTypes = {
  classes: PropTypes.object,
  errors: PropTypes.array.isRequired,
  facebookAccountConnector: PropTypes.object,
  googleAccountConnector: PropTypes.object,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onValidate: PropTypes.func.isRequired,
  refetch: PropTypes.func,
  submitting: PropTypes.bool,
  values: PropTypes.object,
  viewer: PropTypes.object,
};

const ToggleRadio = ({ checked, classes }) => (
  <Radio
    classes={{ root: classes.radioRoot }}
    checked={checked}
    checkedIcon={<RadioChecked />}
    icon={<RadioUnchecked />}
  />
);

ToggleRadio.propTypes = {
  classes: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
};

function AudienceNewPageSyncStepContainer({ viewer, ...props }) {
  const subtype = props.values.subtype;

  const syncToGoogle = audienceSubtypeServices(subtype).includes(
    audienceServices.google,
  );

  const syncToManagedGoogle =
    (viewer?.canAccessGoogleContactAudiences ||
      viewer?.canAccessGoogleManagedVisitorAudiences) &&
    audienceSubtypeServices(subtype).includes(audienceServices.managedGoogle);

  const fetchGoogle = Boolean(syncToGoogle || syncToManagedGoogle);

  const {
    data: { facebookAccountConnector, googleAccountConnector } = {},
    loading,
    refetch,
    error,
  } = useQuery(query, { variables: { fetchGoogle } });

  if (error) {
    return <GraphqlError error={error} refetch={() => refetch} />;
  } else {
    return (
      <AudienceNewPageSyncStep
        facebookAccountConnector={facebookAccountConnector}
        googleAccountConnector={googleAccountConnector}
        loading={loading}
        refetch={refetch}
        syncToGoogle={syncToGoogle}
        syncToManagedGoogle={syncToManagedGoogle}
        {...props}
      />
    );
  }
}

export default withStyles(styles)(AudienceNewPageSyncStepContainer);
